import { digitsOnly } from '@/features/application/types';
import type { ProfileUpdatePayload } from '@/services/candidate';
import type { CandidateProfile } from '@/services/candidateProfile';

/**
 * What the returning candidate's screen holds — their record as they are correcting it.
 *
 * It is a wider set than the create form's [decision 0043's eleven]: a shift preference, a home address and
 * an emergency contact are things a record accumulates and a first application does not collect.
 */
export interface ProfileUpdateValues {
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly phone: string;
  readonly shiftPreference: string;
  readonly payRate: string;
  /** Free text on the screen; the API takes `YYYY-MM-DD` and this is its `startDate`. */
  readonly dateAvailable: string;
  readonly homeAddress: string;
  readonly emergencyContact: string;
  readonly emergencyPhone: string;
  /*
   * ⛔ **No eligibility answer and no consent live here** [0479; 0507]. The returning screen asks neither
   * *18 or older* nor *legally authorized to work*; the frame draws no such row, and those two questions
   * belong to the create [0161; 0195]. The consent is the modal's answer, asked only when the record holds
   * none, and travels beside these values rather than among them.
   */
}

/**
 * Nothing filled in yet — and the screen deliberately overlays NO check-in factor on it [ISS-221]: what was
 * typed at check-in (`garcia`, `Maria.Garcia@Gmail.com`) matched the record without being the record's own
 * value (`Garcia-Lopez`, `maria.garcia@gmail.com`). Both come from the record or are left alone.
 */
export const EMPTY_PROFILE_UPDATE: ProfileUpdateValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  shiftPreference: '',
  payRate: '',
  dateAvailable: '',
  homeAddress: '',
  emergencyContact: '',
  emergencyPhone: '',
};

/**
 * The fields this screen can MARK as wrong.
 *
 * `shiftPreference` is not one, and leaving it out is the point rather than an omission: it is a `<select>`
 * offering Bullhorn's own five values and nothing else, so it cannot hold a value the API refuses, and the
 * shared `Select` primitive draws no error state — giving it one is a change to a shared component against
 * a frame that does not draw it. A refusal naming it would mean the picklist itself moved, and it is
 * carried by the screen's summary line instead of by a border nobody can see.
 */
export type MarkableField = Exclude<keyof ProfileUpdateValues, 'shiftPreference'>;

/** Which fields are wrong, keyed the way the screen renders them. Empty means the form may continue. */
export type ProfileUpdateErrors = Partial<Record<MarkableField, true>>;

/**
 * A phone the API will take: **ten digits, or nothing at all.**
 *
 * Blank is valid HERE and only here. This screen is for corrections, so an untouched field means *"I did
 * not change this"* rather than *"make this empty"*, and the API applies the same asymmetry — `phone` is
 * required on a create and optional on an update.
 *
 * ⚠️ **A number that is not ten digits is refused, never trimmed to ten.** Bullhorn holds numbers this rule
 * cannot express — `+541125484747` was read off a real record on 2026-09-07 — and cutting it to
 * `5411254847` produces a different, wrong, perfectly dialable phone number on somebody's file. Refusing it
 * asks the person standing there to fix it; truncating it would decide for them, silently, and wrongly.
 */
export function isSavablePhone(value: string): boolean {
  return value === '' || /^[0-9]{10}$/.test(digitsOnly(value));
}

/**
 * A pay rate the API will take: **hourly, three digits and two decimals at most, or nothing at all.**
 *
 * `45000` is not a valid answer and never was: decision 0043's 2026-08-05 clarification withdrew the
 * *"or annual salary"* reading, and the value lands in Bullhorn's `hourlyRate`.
 *
 * ⚠️ This screen collected it as free text and **dropped nothing** — it sent whatever was typed straight
 * into the API's `@Matches`, which refused the whole body [0172]. The rate was never the only casualty.
 */
export function isSavablePayRate(value: string): boolean {
  const rate = value.trim();
  return rate === '' || /^[0-9]{1,3}(\.[0-9]{1,2})?$/.test(rate);
}

/**
 * An availability date the API will take: **`YYYY-MM-DD`, any date, or nothing at all** [0180].
 *
 * Two ways it used to be wrong, and neither said so:
 *
 *  - **Malformed.** The control was free text until 2026-09-02 and the send path DROPPED anything that was
 *    not ISO — so a candidate who typed *"next monday"* watched the flow continue with their availability
 *    silently discarded. Refusing it and saying what the field needs is the rule now; dropping a value
 *    somebody typed is the same class of defect as swallowing a failed write.
 *  - ~~**In the past.**~~ **No longer refused** [0172 reversed by 0180]. This rule used to reject a past
 *    date on the grounds that Bullhorn's field means *"When candidate can work"*. It also named its own
 *    counter-example: *"the realistic source is not a typo: it is the record's OWN old availability date,
 *    read back and pre-filled by this screen"* — and then refused it anyway. A returning candidate whose
 *    availability has not changed has nothing to correct, and blocking them on a field they never touched is
 *    the dead end BR-K7 forbids.
 *
 * ⚠️ **The floor lives on the picker and only there** — `DateField`'s `min` is today, so a past day cannot
 * be CHOSEN, while a past day already on the record is shown, saved and accepted by the api. Deliberate
 * [0180]: *"you should not pick an old date, but you can have an old date."* Do not add a range check here
 * to match the calendar.
 */
export function isSavableStartDate(value: string): boolean {
  const date = value.trim();
  if (date === '') return true;
  return /^\d{4}-\d{2}-\d{2}$/.test(date);
}

/**
 * Validates the corrections before they are sent.
 *
 * **Every rule here is one the API would refuse the whole request over, and there are no others**
 * [decision 0172]. Blank always passes: `us-kap-7` says these fields *"record whatever was entered and let
 * the candidate through, including when left blank"*, and demanding a complete profile from somebody who
 * came to fix one thing is how a correction gets refused over five fields they never touched.
 *
 * `firstName`, `lastName`, `email`, `homeAddress` and `emergencyContact` carry no shape at all — any text
 * is a name — and `shiftPreference` is a `<select>` of Bullhorn's own five values, so none of them can be
 * malformed. That is why they are absent rather than forgotten.
 */
export function validateProfileUpdate(values: ProfileUpdateValues): ProfileUpdateErrors {
  const errors: ProfileUpdateErrors = {};
  if (!isSavablePhone(values.phone)) errors.phone = true;
  if (!isSavablePhone(values.emergencyPhone)) errors.emergencyPhone = true;
  if (!isSavablePayRate(values.payRate)) errors.payRate = true;
  if (!isSavableStartDate(values.dateAvailable)) errors.dateAvailable = true;
  return errors;
}

/**
 * What Bullhorn already holds, laid over what the candidate has typed.
 *
 * **Only fields they have NOT touched are filled**, so a read that lands late can never overwrite
 * something they just typed. Fails open: an unreadable profile leaves the form empty and they carry on
 * [BR-K7].
 *
 * Pure, and separate from the screen, because the values it produces are also the ones that get VALIDATED
 * on arrival — `validateProfileUpdate(mergeOnFile(EMPTY_PROFILE_UPDATE, onFile))` is exactly "which of the
 * values on this record would the API refuse", which is what the screen marks before the candidate has
 * touched anything [0172].
 */
export function mergeOnFile(
  current: ProfileUpdateValues,
  onFile: CandidateProfile,
): ProfileUpdateValues {
  const filled = (mine: string, theirs: string | undefined): string =>
    mine === '' ? (theirs ?? '') : mine;
  return {
    firstName: filled(current.firstName, onFile.firstName),
    lastName: filled(current.lastName, onFile.lastName),
    email: filled(current.email, onFile.email),
    /*
     * Normalised on the way IN, and then LEFT ALONE. The record may hold `+541125484747`; the plus is not
     * a digit and Bullhorn's own rule has no room for it, so it goes — but the twelve digits stay twelve.
     * Trimming them to ten produces `5411254847`, a different, dialable, wrong number [0172].
     */
    phone: filled(current.phone, digitsOnly(onFile.phone ?? '')),
    shiftPreference: filled(current.shiftPreference, onFile.shiftPreference),
    payRate: filled(current.payRate, onFile.payRate),
    dateAvailable: filled(current.dateAvailable, onFile.startDate),
    // The STREET line, not city/state/ZIP joined together. The first version built that string, which read
    // plausibly and would have written a city into the street field the moment it was wired up.
    homeAddress: filled(current.homeAddress, onFile.homeAddress),
    emergencyContact: filled(current.emergencyContact, onFile.emergencyContact),
    emergencyPhone: filled(current.emergencyPhone, digitsOnly(onFile.emergencyPhone ?? '')),
  };
}

/** The changed fields of a returning save, under the api's names — everything but the id and the consent. */
export type ProfileChanges = Omit<ProfileUpdatePayload, 'candidateId' | 'consent'>;

/**
 * **Only what the candidate changed against the loaded record** [0464 R3–R7].
 *
 * - **Untouched → absent** (R3–R5): a field equal to the record's value is not sent — never `''`, and never
 *   the record's value written back over itself. Nothing changed is an empty object, and no write at all.
 * - **Empty → absent** (R6, R7): a field the record never held and the candidate left empty is not sent, and
 *   a field they CLEARED is not sent either — clearing is not a way to erase a Bullhorn value from a kiosk.
 * - **A read that failed** leaves `baseline` empty, so only what they typed travels [0464 rule 2].
 *
 * Both sides are compared as they would be SENT — trimmed, phones as digits — so a stray space or the
 * record's `+` is not a change. City, state and ZIP are not here at all: this screen does not draw them.
 */
export function changesFrom(
  values: ProfileUpdateValues,
  baseline: ProfileUpdateValues,
): ProfileChanges {
  const changes: { -readonly [K in keyof ProfileChanges]: ProfileChanges[K] } = {};
  const put = (
    key: keyof ProfileChanges,
    field: keyof ProfileUpdateValues,
    normalise: (value: string) => string,
  ): void => {
    const mine = normalise(values[field]);
    if (mine !== '' && mine !== normalise(baseline[field])) changes[key] = mine;
  };
  const trimmed = (value: string): string => value.trim();
  put('firstName', 'firstName', trimmed);
  put('lastName', 'lastName', trimmed);
  put('email', 'email', trimmed);
  put('phone', 'phone', digitsOnly);
  put('payRate', 'payRate', trimmed);
  // A picklist value on Bullhorn's side, so the screen offers their five and sends nothing else.
  put('shiftPreference', 'shiftPreference', trimmed);
  put('homeAddress', 'homeAddress', trimmed);
  put('emergencyContact', 'emergencyContact', trimmed);
  put('emergencyPhone', 'emergencyPhone', digitsOnly);
  // ISO or empty — `validateProfileUpdate` has already guaranteed it [0172, 0180].
  put('startDate', 'dateAvailable', trimmed);
  return changes;
}

/**
 * The API's field names, translated into the ones this screen draws.
 *
 * Only `startDate` differs, and that difference is the reason this map exists rather than a cast: the
 * control is labelled *Date available* here.
 */
const FIELD_BY_API_NAME: Readonly<Record<string, MarkableField>> = {
  firstName: 'firstName',
  lastName: 'lastName',
  email: 'email',
  phone: 'phone',
  payRate: 'payRate',
  startDate: 'dateAvailable',
  homeAddress: 'homeAddress',
  emergencyContact: 'emergencyContact',
  emergencyPhone: 'emergencyPhone',
};

/**
 * What a refused save marks on the form.
 *
 * A name this screen does not draw maps to nothing **on purpose** — the candidate cannot fix a control that
 * is not in front of them, and a red border on the nearest field would be a guess. Those cases are carried
 * by the screen's summary line instead, which is the honest answer.
 */
export function rejectionErrors(fields: readonly string[]): ProfileUpdateErrors {
  const errors: ProfileUpdateErrors = {};
  for (const field of fields) {
    const key = FIELD_BY_API_NAME[field];
    if (key !== undefined) errors[key] = true;
  }
  return errors;
}

/**
 * Drops one field's error, leaving every other one alone.
 *
 * Editing a field is the candidate answering its message, so the message goes; the next attempt puts it
 * back if they have not. Clearing the whole set instead would hide a second problem they have not reached
 * yet, which on a kiosk means a Continue that fails twice for two different reasons.
 */
export function clearError(
  errors: ProfileUpdateErrors,
  key: keyof ProfileUpdateValues,
): ProfileUpdateErrors {
  if (key === 'shiftPreference' || errors[key] === undefined) return errors;
  const next = { ...errors };
  delete next[key];
  return next;
}
